import { useEffect, useRef, useState } from "react";

// Awwwards-caliber custom cursor: dot + expanding hover + optional cat-tail SVG on image hover.
export default function CustomCursor() {
  const dotRef = useRef(null);
  const tailRef = useRef(null);
  const labelRef = useRef(null);
  const posRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef({ x: 0, y: 0 });
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    // Disable on touch devices
    const isTouch = window.matchMedia("(hover: none), (pointer: coarse)").matches;
    if (isTouch) {
      setEnabled(false);
      return;
    }

    const dot = dotRef.current;
    const tail = tailRef.current;
    const label = labelRef.current;

    const onMove = (e) => {
      targetRef.current.x = e.clientX;
      targetRef.current.y = e.clientY;
    };
    window.addEventListener("mousemove", onMove, { passive: true });

    // Smooth follow
    let raf;
    const tick = () => {
      posRef.current.x += (targetRef.current.x - posRef.current.x) * 0.22;
      posRef.current.y += (targetRef.current.y - posRef.current.y) * 0.22;
      const { x, y } = posRef.current;
      if (dot) dot.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      if (tail) tail.style.transform = `translate(${x}px, ${y + 6}px) translate(-50%, -50%)`;
      if (label) label.style.transform = `translate(${x}px, ${y + 46}px) translate(-50%, -50%)`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // Hover detection using event delegation
    const onOver = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const image = t.closest("[data-cursor='image']");
      const view = t.closest("[data-cursor='view']");
      const interactive = t.closest("a, button, [role='button'], input, textarea, select, label, [data-cursor='hover']");

      dot.classList.remove("is-hover", "is-image");
      tail.classList.remove("visible");
      label.classList.remove("visible");
      label.textContent = "";

      if (image) {
        dot.classList.add("is-image");
        tail.classList.add("visible");
        label.textContent = view ? view.dataset.label || "Ver" : "Ver";
        label.classList.add("visible");
      } else if (interactive) {
        dot.classList.add("is-hover");
      }
    };
    document.addEventListener("mouseover", onOver);

    // Hide when leaving window
    const onLeave = () => {
      dot.style.opacity = "0";
      tail.style.opacity = "0";
    };
    const onEnter = () => {
      dot.style.opacity = "1";
    };
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      <div ref={dotRef} className="cursor-dot" aria-hidden="true" />
      {/* Cat tail SVG hint that appears on image hover */}
      <svg
        ref={tailRef}
        className="cursor-tail"
        width="120"
        height="60"
        viewBox="0 0 120 60"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M2 30 C 30 8, 60 8, 90 30 C 100 36, 112 34, 118 26"
          stroke="#FAF8F5"
          strokeWidth="1"
          strokeLinecap="round"
          fill="none"
          opacity="0.9"
        />
      </svg>
      <div ref={labelRef} className="cursor-label" aria-hidden="true" />
    </>
  );
}
